import { useState } from "react";
import { PDFDocument, rgb } from "pdf-lib";
import { TextKit } from "./pdfText";
import { todayIso } from "./legalClauses";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Download, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { CURRENCY_CHOICES, fmtMoney } from "@/tools/converters/money";
import { receiptTotals, receiptLineProblem, hasEmoji } from "./receiptCalc";
import { ProjectBar } from "@/components/ProjectBar";
import { useProject } from "@/lib/workflow/useProject";
import { incomeFromDocument } from "@/lib/workflow/ledgerEntry";
import { NoticeBox } from "@/components/NoticeBox";

interface LineItem {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

const TOOL_INFO_CONTENT = {
  what: `Creates a formatted receipt for a transaction, with business details, itemized purchases, and a total, ready to print or send as a PDF.`,
  how: `Enter the transaction details and line items. Preview the receipt, then download it as a PDF.`,
  tip: `Include a receipt number and date on every receipt you generate \u2014 it's a small habit that makes reconciling records against bank statements much easier later.`,
};

export default function ReceiptMaker() {
  const [businessName, setBusinessName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("R-001");
  const [date, setDate] = useState(todayIso());
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [cardLast4, setCardLast4] = useState("");
  const [upiReference, setUpiReference] = useState("");
  const [bankTransferDate, setBankTransferDate] = useState("");
  const [items, setItems] = useState<LineItem[]>([{ id: "1", description: "", quantity: "1", unitPrice: "" }]);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [taxPct, setTaxPct] = useState("0");
  const [taxLabel, setTaxLabel] = useState("Tax");

  const addItem = () => setItems(prev => [...prev, { id: Date.now().toString(), description: "", quantity: "1", unitPrice: "" }]);
  const removeItem = (id: string) => setItems(prev => prev.filter(i => i.id !== id));
  const updateItem = (id: string, field: keyof LineItem, value: string) =>
    setItems(prev => prev.map(i => (i.id === id ? { ...i, [field]: value } : i)));

  const money = (n: number) => fmtMoney(n, currency);
  const [refund, setRefund] = useState(false);
  const totals = receiptTotals(items.filter(i => i.description.trim() && i.quantity.trim() && i.unitPrice.trim()), taxPct, refund);
  const lineProblem = receiptLineProblem(items);
  const emojiNote = hasEmoji([businessName, customerName, ...items.map(i => i.description)].join(" "));
  const total = totals.total;
  const taxName = taxLabel.trim() || "Tax";

  // Every item row that has ANY field filled in must have all three --
  // previously a row with just a description and no price would be
  // silently skipped at generation time with no explanation, which
  // could mean a real item quietly missing from the total.
  const completeItems = items.filter(i => i.description.trim() && i.quantity.trim() && i.unitPrice.trim());
  const partialItems = items.filter(i => (i.description.trim() || i.quantity.trim() || i.unitPrice.trim()) && !(i.description.trim() && i.quantity.trim() && i.unitPrice.trim()));
  const missingRequired = !businessName.trim() || completeItems.length === 0 || partialItems.length > 0 || !!lineProblem;

  const { logUsageNow } = useLogUsageOnAction();
  const [, projectStore] = useProject();
  const addToLedger = () => {
    const e = incomeFromDocument({ kind: "receipt", number: receiptNumber.trim() || "R", date, party: customerName, total, currency, taxPct: parseFloat(taxPct) || 0, taxLabel });
    if (!e) { toast.error("Add at least one complete item first."); return; }
    projectStore.upsertRecords("expenses", [e]);
    toast.success("Added to your project as income. Open the Expense Tracker to see it.");
  };

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating, while handleGenerate (builds and downloads the real PDF)
  // had zero usage-limit check of its own.
  const handleGenerate = async () => {
    // Validate first: a missing field must not use up a daily credit.
    if (lineProblem) { toast.error(lineProblem); return; }
    if (missingRequired) {
      toast.error(partialItems.length > 0 ? "Every item needs a description, quantity, and price filled in." : "Please fill in your business name and at least one complete item.");
      return;
    }
    const allowed = await logUsageNow();
    if (!allowed) return;
    try {

    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([300, 500]); // narrow receipt-style page
    const kit = await TextKit.create(pdfDoc, [businessName, customerName, receiptNumber, date, paymentMethod, cardLast4, upiReference, bankTransferDate,
      ...completeItems.map(i => i.description), "RECEIPT REFUND Receipt Date Customer Payment Card UPI Ref Transfer TOTAL Subtotal Tax Thank you! Received by Date 0123456789 x = $ . %", taxLabel, money(1234.56)].join("\n"));

    let y = 470;
    const left = 20;
    const right = 280;

    const centerText = (text: string, size: number, bold = false) => {
      const width = kit.width(text, size, bold);
      kit.draw(page, text, { x: (300 - width) / 2, y, size, bold });
    };

    centerText(businessName, 14, true);
    y -= 20;
    centerText(refund ? "REFUND RECEIPT" : "RECEIPT", 10);
    y -= 24;

    kit.draw(page, `Receipt #: ${receiptNumber}`, { x: left, y, size: 9 });
    y -= 14;
    kit.draw(page, `Date: ${date}`, { x: left, y, size: 9 });
    y -= 14;
    if (customerName) {
      kit.draw(page, `Customer: ${customerName}`, { x: left, y, size: 9 });
      y -= 14;
    }
    kit.draw(page, `Payment: ${paymentMethod}`, { x: left, y, size: 9 });
    y -= 14;
    if ((paymentMethod === "Credit Card" || paymentMethod === "Debit Card") && cardLast4) {
      kit.draw(page, `Card: **** **** **** ${cardLast4}`, { x: left, y, size: 9 });
      y -= 14;
    }
    if (paymentMethod === "UPI" && upiReference) {
      kit.draw(page, `UPI Ref: ${upiReference}`, { x: left, y, size: 9 });
      y -= 14;
    }
    if (paymentMethod === "Bank Transfer" && bankTransferDate) {
      kit.draw(page, `Transfer Date: ${bankTransferDate}`, { x: left, y, size: 9 });
      y -= 14;
    }
    page.drawLine({ start: { x: left, y }, end: { x: right, y }, thickness: 0.5, color: rgb(0.7, 0.7, 0.7) });
    y -= 16;

    for (let n = 0; n < completeItems.length; n++) {
      const item = completeItems[n];
      const ln = totals.lines[n];
      kit.draw(page, item.description.slice(0, 28), { x: left, y, size: 9 });
      y -= 12;
      const lineTotal = `${ln.qty} x ${money(Math.abs(ln.price))} = ${money(ln.amount)}`;
      kit.drawRight(page, lineTotal, { rightX: right, y, size: 9 });
      y -= 16;
    }

    y -= 4;
    page.drawLine({ start: { x: left, y }, end: { x: right, y }, thickness: 0.5, color: rgb(0.7, 0.7, 0.7) });
    y -= 20;

    if (totals.tax !== 0) {
      kit.draw(page, "Subtotal", { x: left, y, size: 9 });
      kit.drawRight(page, money(totals.subtotal), { rightX: right, y, size: 9 });
      y -= 14;
      kit.draw(page, `${taxName} (${Number(taxPct)}%)`, { x: left, y, size: 9 });
      kit.drawRight(page, money(totals.tax), { rightX: right, y, size: 9 });
      y -= 18;
    }
    kit.draw(page, "TOTAL", { x: left, y, size: 11, bold: true });
    const totalText = money(total);
    kit.drawRight(page, totalText, { rightX: right, y, size: 11, bold: true });
    y -= 30;

    centerText("Thank you!", 9);

    if (includeSignature) {
      // The receipt page is narrow and short (a real receipt-strip
      // proportion, not a full letter page) -- a longer item list can
      // leave very little room below "Thank you!" for a signature line,
      // so start a fresh page rather than letting it run off the bottom.
      const SIGNATURE_BLOCK_HEIGHT = 60;
      let sigPage = page;
      let sigY = y - 30;
      if (sigY < SIGNATURE_BLOCK_HEIGHT) {
        sigPage = pdfDoc.addPage([300, 500]);
        sigY = 470;
      }
      kit.draw(sigPage, "Received by: ________________", { x: left, y: sigY, size: 9 });
      sigY -= 16;
      kit.draw(sigPage, "Date: _____________", { x: left, y: sigY, size: 9 });
    }

    await kit.finish();
    const pdfBytes = await pdfDoc.save();
    downloadBlob(new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" }), `${receiptNumber || "receipt"}.pdf`);
    const note = kit.note();
    if (note) toast.warning(note); else toast.success("Receipt generated!");
    } catch (err) {
      console.error("Receipt PDF failed", err);
      toast.error("Couldn't build the PDF. Try removing unusual symbols from the text.");
    }
  };

  return (
    <ToolContainer>
      <div className="max-w-xl mx-auto space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Business Name *</Label><Input aria-label="Business Name" value={businessName} onChange={e => setBusinessName(e.target.value)} /></div>
          <div><Label>Customer Name (optional)</Label><Input aria-label="Customer Name (optional)" value={customerName} onChange={e => setCustomerName(e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Receipt #</Label><Input aria-label="Receipt #" value={receiptNumber} onChange={e => setReceiptNumber(e.target.value)} /></div>
          <div><Label>Date</Label><Input aria-label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
          <div>
            <Label>Payment Method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="Credit Card">Credit Card</SelectItem>
                <SelectItem value="Debit Card">Debit Card</SelectItem>
                <SelectItem value="UPI">UPI</SelectItem>
                <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                <SelectItem value="Check">Check</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(paymentMethod === "Credit Card" || paymentMethod === "Debit Card") && (
            <div>
              <Label>Card Last 4 Digits (optional)</Label>
              <Input aria-label="Card Last 4 Digits (optional)"
                value={cardLast4}
                onChange={e => setCardLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="1234"
                maxLength={4}
                inputMode="numeric"
              />
            </div>
          )}
          {paymentMethod === "UPI" && (
            <div>
              <Label>UPI Transaction Reference (optional)</Label>
              <Input aria-label="UPI Transaction Reference (optional)" value={upiReference} onChange={e => setUpiReference(e.target.value)} placeholder="e.g. last 4 digits + date, or UTR number" />
            </div>
          )}
          {paymentMethod === "Bank Transfer" && (
            <div>
              <Label>Transfer Date (optional)</Label>
              <Input aria-label="Transfer Date (optional)" type="date" value={bankTransferDate} onChange={e => setBankTransferDate(e.target.value)} />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Items *</Label>
          {items.map(item => {
            const isPartial = partialItems.some(p => p.id === item.id);
            return (
              <div key={item.id} className={`flex gap-2 items-start p-1 rounded-md ${isPartial ? "ring-1 ring-amber-400/60" : ""}`}>
                <Input placeholder="Description" value={item.description} onChange={e => updateItem(item.id, "description", e.target.value)} className="flex-1" />
                <Input type="number" aria-label="Quantity" placeholder="Qty" value={item.quantity} onChange={e => updateItem(item.id, "quantity", e.target.value)} className="w-16" />
                <Input type="number" aria-label="Unit price" placeholder="Price" value={item.unitPrice} onChange={e => updateItem(item.id, "unitPrice", e.target.value)} className="w-24" />
                <Button size="icon" variant="ghost" onClick={() => removeItem(item.id)} disabled={items.length === 1}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={addItem} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add Item
          </Button>
          {lineProblem && <p role="alert" className="text-xs text-destructive" data-testid="rcp-line-problem">{lineProblem}</p>}
          {emojiNote && <p className="text-xs text-muted-foreground" data-testid="rcp-emoji-note">Emoji and pictures can't be printed on the PDF receipt and will be left out.</p>}
          {partialItems.length > 0 && (
            <NoticeBox className="text-xs">Highlighted item(s) are missing a description, quantity, or price — fill in all three or remove the row.</NoticeBox>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Currency</Label>
            <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Currency" value={currency} onChange={e => setCurrency(e.target.value)}>
              {CURRENCY_CHOICES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div><Label>Tax (%)</Label><Input aria-label="Tax (%)" inputMode="decimal" value={taxPct} onChange={e => setTaxPct(e.target.value)} /></div>
          <div><Label>Tax label</Label><Input aria-label="Tax label" value={taxLabel} onChange={e => setTaxLabel(e.target.value)} placeholder="GST / VAT" /></div>
        </div>

        <div className="p-4 rounded-xl border bg-primary/5 text-right">
          <p className="text-xs text-muted-foreground">Total</p>
          {totals.tax !== 0 && <p className="text-xs text-muted-foreground">Subtotal {money(totals.subtotal)} + {taxName} {money(totals.tax)}</p>}
          <p className="text-2xl font-bold text-primary">{money(total)}</p>
        </div>

        <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="w-full gap-2">
          {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
        </Button>

        {showPreview && !missingRequired && (
          <div className="border rounded-lg bg-muted/20 p-4 flex justify-center">
            <div className="bg-white text-black rounded-md shadow-sm p-5 w-[260px] text-xs font-mono">
              <p className="text-center font-bold text-sm">{businessName}</p>
              <p className="text-center text-[10px] mb-2">{refund ? "REFUND RECEIPT" : "RECEIPT"}</p>
              <p>Receipt #: {receiptNumber}</p>
              <p>Date: {date}</p>
              {customerName && <p>Customer: {customerName}</p>}
              <p>Payment: {paymentMethod}</p>
              {(paymentMethod === "Credit Card" || paymentMethod === "Debit Card") && cardLast4 && <p>Card: **** **** **** {cardLast4}</p>}
              {paymentMethod === "UPI" && upiReference && <p>UPI Ref: {upiReference}</p>}
              {paymentMethod === "Bank Transfer" && bankTransferDate && <p>Transfer Date: {bankTransferDate}</p>}
              <div className="border-t border-dashed border-black/30 my-2" />
              {completeItems.map((item, n) => {
                const ln = totals.lines[n];
                return (
                  <div key={item.id} className="flex justify-between gap-2 mb-1">
                    <span className="truncate">{item.description}</span>
                    <span className="shrink-0">{ln.qty} x {money(Math.abs(ln.price))} = {money(ln.amount)}</span>
                  </div>
                );
              })}
              <div className="border-t border-dashed border-black/30 my-2" />
              <div className="flex justify-between font-bold text-sm">
                <span>TOTAL</span>
                <span>{money(total)}</span>
              </div>
              <p className="text-center mt-3 text-[10px]">Thank you!</p>
            </div>
          </div>
        )}

        {missingRequired && (
          <NoticeBox className="text-xs text-center">Business name and at least one complete item are required before you can preview or download.</NoticeBox>
        )}

        <div className="flex items-center gap-2">
          <Checkbox id="refund-mode" checked={refund} onCheckedChange={(v) => setRefund(v === true)} />
          <Label htmlFor="refund-mode" className="font-normal cursor-pointer">Refund receipt (amounts are shown as negative)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="include-signature" checked={includeSignature} onCheckedChange={(v) => setIncludeSignature(v === true)} />
          <Label htmlFor="include-signature" className="font-normal cursor-pointer">Include a "Received by" signature line</Label>
        </div>

        <Button onClick={handleGenerate} disabled={missingRequired} className="w-full gap-2">
          <Download className="h-4 w-4" /> Generate & Download Receipt (PDF)
        </Button>
        <Button variant="outline" onClick={addToLedger} disabled={missingRequired} className="w-full" data-testid="rcp-ledger">Add to project ledger as income</Button>
      </div>
      <ProjectBar />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
